import React from "react";
import { Accordion, Badge, Box, Code, Group, Stack, Text } from "@mantine/core";
import { EmptyState, SectionCard, spacePx } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { CollectionDiff, ConfigDiff, FieldChange, ScalarDiff } from "@models/mappingConfig";
import { AdminTable, col } from "@components/AdminTable";
import { formatValue } from "./format";

const lineColor = (line: string): { bg?: string; c?: string } => {
  if (line.startsWith("+++") || line.startsWith("---")) return { c: "dimmed" };
  if (line.startsWith("@@")) return { c: "blue.7", bg: "var(--mantine-color-blue-light)" };
  if (line.startsWith("+")) return { bg: "var(--mantine-color-green-light)", c: "green.9" };
  if (line.startsWith("-")) return { bg: "var(--mantine-color-red-light)", c: "red.9" };
  return {};
};

/** A unified diff (`@@ ... @@` hunks), one line per row, added green and removed red. */
export const UnifiedDiff: React.FC<{ unified: string }> = ({ unified }) => (
  <Box
    component="pre"
    m={0}
    p="xs"
    style={{ overflowX: "auto", border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-xs)" }}
    data-testid="unified-diff"
  >
    {unified.split("\n").map((line, index) => {
      const { bg, c } = lineColor(line);
      return (
        <Text key={index} component="div" size="xs" ff="monospace" c={c} bg={bg} px={spacePx[1]}
              style={{ whiteSpace: "pre" }} data-diff-line={line[0] ?? " "}>
          {line || " "}
        </Text>
      );
    })}
  </Box>
);

const ValueText: React.FC<{ value: unknown; tone?: "old" | "new" }> = ({ value, tone }) => (
  <Text size="sm" ff="monospace" truncate title={formatValue(value)}
        c={tone === "old" ? "red.8" : tone === "new" ? "green.8" : undefined}>
    {value === undefined ? "-" : formatValue(value) || '""'}
  </Text>
);

const FieldChangesTable: React.FC<{ fields: FieldChange[] }> = ({ fields }) => {
  const { t } = useTranslation();
  return (
    <AdminTable<FieldChange>
      columns={[
        col.text<FieldChange>("path", t("mappingConfig.diff.field"), (f) => f.path, { mono: true, width: "28%" }),
        { key: "old", header: t("mappingConfig.diff.old"), render: (f) => <ValueText value={f.old} tone="old" /> },
        { key: "new", header: t("mappingConfig.diff.new"), render: (f) => <ValueText value={f.new} tone="new" /> },
      ]}
      data={fields}
      getRowKey={(f) => f.path}
    />
  );
};

const itemSummary = (item: Record<string, unknown>, key: string): string =>
  Object.entries(item)
    .filter(([name]) => name !== key)
    .map(([name, value]) => `${name}: ${formatValue(value)}`)
    .join(" · ");

const CollectionSection: React.FC<{ collection: CollectionDiff }> = ({ collection }) => {
  const { t } = useTranslation();
  const { key } = collection;
  type Row = { key: string | number; item: Record<string, unknown> };
  const listColumns = [
    col.text<Row>("key", key, (r) => String(r.key), { mono: true, width: 112 }),
    col.text<Row>("item", t("mappingConfig.diff.item"), (r) => itemSummary(r.item, key)),
  ];
  return (
    <SectionCard
      data-collection-diff={collection.path}
      title={
        <Group gap="xs">
          <Text fw={600} ff="monospace">{collection.path}</Text>
          <Badge variant="light" color="green">{t("mappingConfig.diff.addedCount", { count: collection.added.length })}</Badge>
          <Badge variant="light" color="red">{t("mappingConfig.diff.removedCount", { count: collection.removed.length })}</Badge>
          <Badge variant="light" color="blue">{t("mappingConfig.diff.changedCount", { count: collection.changed.length })}</Badge>
        </Group>
      }
    >
      <Stack gap="md">
        {collection.added.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="green.8">{t("mappingConfig.diff.added")}</Text>
            <AdminTable<Row> columns={listColumns} data={collection.added} getRowKey={(r) => String(r.key)} />
          </Stack>
        )}
        {collection.removed.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="red.8">{t("mappingConfig.diff.removed")}</Text>
            <AdminTable<Row> columns={listColumns} data={collection.removed} getRowKey={(r) => String(r.key)} />
          </Stack>
        )}
        {collection.changed.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600} c="blue.8">{t("mappingConfig.diff.changed")}</Text>
            <Accordion variant="separated" multiple chevronPosition="left">
              {collection.changed.map((row) => (
                <Accordion.Item key={String(row.key)} value={String(row.key)}>
                  <Accordion.Control>
                    <Group gap="xs">
                      <Code>{String(row.key)}</Code>
                      <Text size="sm" c="dimmed">{row.fields.map((f) => f.path).join(", ")}</Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <FieldChangesTable fields={row.fields} />
                  </Accordion.Panel>
                </Accordion.Item>
              ))}
            </Accordion>
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};

/** One rendering of a config diff for every service: scalars, prompt texts, collections. */
export const ConfigDiffView: React.FC<{ diff: ConfigDiff }> = ({ diff }) => {
  const { t } = useTranslation();
  const empty = diff.scalars.length === 0 && diff.texts.length === 0 && diff.collections.length === 0;
  if (empty) return <EmptyState title={t("mappingConfig.diff.empty")} />;

  return (
    <Stack gap="md">
      {diff.scalars.length > 0 && (
        <SectionCard title={t("mappingConfig.diff.scalars")}>
          <AdminTable<ScalarDiff>
            columns={[
              col.twoLine<ScalarDiff>("path", t("mappingConfig.diff.field"), (s) => ({ primary: s.path, secondary: s.group }),
                                       { width: "32%", mono: true }),
              { key: "old", header: t("mappingConfig.diff.old"), render: (s) => <ValueText value={s.old} tone="old" /> },
              { key: "new", header: t("mappingConfig.diff.new"), render: (s) => <ValueText value={s.new} tone="new" /> },
            ]}
            data={diff.scalars}
            getRowKey={(s) => s.path}
          />
        </SectionCard>
      )}
      {diff.texts.map((text) => (
        <SectionCard key={text.path} title={<Text fw={600} ff="monospace">{text.path}</Text>}>
          <UnifiedDiff unified={text.unified} />
        </SectionCard>
      ))}
      {diff.collections.map((collection) => (
        <CollectionSection key={collection.path} collection={collection} />
      ))}
    </Stack>
  );
};
